// =============================================================================
// COMPANY GROUPS — familiarity, trust, approval
// Familiarity = one categorical group per company.
// Trust / Approval = likert_grid parent + hidden individual likerts per company.
// =============================================================================

// Shared blue gradient for all familiarity charts
const FAMILIARITY_COLORS = {
  never_heard:         "#d0e4f7",
  heard_Know_nothing:  "#90c3e8",
  slightly_familiar:   "#4a9fd4",
  moderately_familiar: "#1a6faa",
  very_familiar:       "#0a3d6b",
};

// Per-company display config (used for grid sub-labels & sub-colors)
const COMPANY_META = {
  air_new_zealand: { label: "Air New Zealand", color: "#00529B" },
  fonterra:        { label: "Fonterra",        color: "#5D4037" },
  foodstuffs:      { label: "Foodstuffs",      color: "#E30613" },
  genesis:         { label: "Genesis Energy",  color: "#F57C00" },
};

export const COMPANY_GROUPS = {

  // ===========================================================================
  // FAMILIARITY — separate categorical group per company
  // ===========================================================================

  air_new_zealand_familiarity: {
    type: "categorical",
    fixedOrder: true,
    label: "Familiarity with Air New Zealand",
    fulltext: "How familiar, if at all, are you with the following companies? Air New Zealand",
    weightCol: "n_raked",
    hidden: true,
    keys: [
      "air_new_zealand_familiar_very_familiar",
      "air_new_zealand_familiar_moderately_familiar",
      "air_new_zealand_familiar_slightly_familiar",
      "air_new_zealand_familiar_heard_Know_nothing",
      "air_new_zealand_familiar_never_heard",
    ],
    labels: {
      air_new_zealand_familiar_never_heard:         "Never heard of them",
      air_new_zealand_familiar_heard_Know_nothing:  "Heard of, know nothing",
      air_new_zealand_familiar_slightly_familiar:   "Slightly familiar",
      air_new_zealand_familiar_moderately_familiar: "Moderately familiar",
      air_new_zealand_familiar_very_familiar:       "Very familiar",
    },
    colors: {
      air_new_zealand_familiar_never_heard:         FAMILIARITY_COLORS.never_heard,
      air_new_zealand_familiar_heard_Know_nothing:  FAMILIARITY_COLORS.heard_Know_nothing,
      air_new_zealand_familiar_slightly_familiar:   FAMILIARITY_COLORS.slightly_familiar,
      air_new_zealand_familiar_moderately_familiar: FAMILIARITY_COLORS.moderately_familiar,
      air_new_zealand_familiar_very_familiar:       FAMILIARITY_COLORS.very_familiar,
    },
  },

  fonterra_familiarity: {
    type: "categorical",
    fixedOrder: true,
    label: "Familiarity with Fonterra",
    fulltext: "How familiar, if at all, are you with the following companies? Fonterra",
    weightCol: "n_raked",
    keys: [
      "fonterra_familiar_very_familiar",
      "fonterra_familiar_moderately_familiar",
      "fonterra_familiar_slightly_familiar",
      "fonterra_familiar_heard_Know_nothing",
      "fonterra_familiar_never_heard",
    ],
    labels: {
      fonterra_familiar_never_heard:         "Never heard of them",
      fonterra_familiar_heard_Know_nothing:  "Heard of, know nothing",
      fonterra_familiar_slightly_familiar:   "Slightly familiar",
      fonterra_familiar_moderately_familiar: "Moderately familiar",
      fonterra_familiar_very_familiar:       "Very familiar",
    },
    colors: {
      fonterra_familiar_never_heard:         FAMILIARITY_COLORS.never_heard,
      fonterra_familiar_heard_Know_nothing:  FAMILIARITY_COLORS.heard_Know_nothing,
      fonterra_familiar_slightly_familiar:   FAMILIARITY_COLORS.slightly_familiar,
      fonterra_familiar_moderately_familiar: FAMILIARITY_COLORS.moderately_familiar,
      fonterra_familiar_very_familiar:       FAMILIARITY_COLORS.very_familiar,
    },
  },

  foodstuffs_familiarity: {
    type: "categorical",
    fixedOrder: true,
    label: "Familiarity with Foodstuffs",
    fulltext: "How familiar, if at all, are you with the following companies? Foodstuffs",
    weightCol: "n_raked",
    keys: [
      "foodstuffs_familiar_very_familiar",
      "foodstuffs_familiar_moderately_familiar",
      "foodstuffs_familiar_slightly_familiar",
      "foodstuffs_familiar_heard_Know_nothing",
      "foodstuffs_familiar_never_heard",
    ],
    labels: {
      foodstuffs_familiar_never_heard:         "Never heard of them",
      foodstuffs_familiar_heard_Know_nothing:  "Heard of, know nothing",
      foodstuffs_familiar_slightly_familiar:   "Slightly familiar",
      foodstuffs_familiar_moderately_familiar: "Moderately familiar",
      foodstuffs_familiar_very_familiar:       "Very familiar",
    },
    colors: {
      foodstuffs_familiar_never_heard:         FAMILIARITY_COLORS.never_heard,
      foodstuffs_familiar_heard_Know_nothing:  FAMILIARITY_COLORS.heard_Know_nothing,
      foodstuffs_familiar_slightly_familiar:   FAMILIARITY_COLORS.slightly_familiar,
      foodstuffs_familiar_moderately_familiar: FAMILIARITY_COLORS.moderately_familiar,
      foodstuffs_familiar_very_familiar:       FAMILIARITY_COLORS.very_familiar,
    },
  },

  genesis_familiarity: {
    type: "categorical",
    fixedOrder: true,
    label: "Familiarity with Genesis Energy",
    fulltext: "How familiar, if at all, are you with the following companies? Genesis Energy",
    weightCol: "n_raked",
    keys: [
      "genesis_familiar_very_familiar",
      "genesis_familiar_moderately_familiar",
      "genesis_familiar_slightly_familiar",
      "genesis_familiar_heard_Know_nothing",
      "genesis_familiar_never_heard",
    ],
    labels: {
      genesis_familiar_never_heard:         "Never heard of them",
      genesis_familiar_heard_Know_nothing:  "Heard of, know nothing",
      genesis_familiar_slightly_familiar:   "Slightly familiar",
      genesis_familiar_moderately_familiar: "Moderately familiar",
      genesis_familiar_very_familiar:       "Very familiar",
    },
    colors: {
      genesis_familiar_never_heard:         FAMILIARITY_COLORS.never_heard,
      genesis_familiar_heard_Know_nothing:  FAMILIARITY_COLORS.heard_Know_nothing,
      genesis_familiar_slightly_familiar:   FAMILIARITY_COLORS.slightly_familiar,
      genesis_familiar_moderately_familiar: FAMILIARITY_COLORS.moderately_familiar,
      genesis_familiar_very_familiar:       FAMILIARITY_COLORS.very_familiar,
    },
  },

  // ===========================================================================
  // TRUST — likert_grid parent (references sub-variables below)
  // ===========================================================================

  company_trust: {
    type: "likert_grid",
    label: "Trust in businesses",
    fulltext: "To what extent do you trust or distrust the following companies?",
    weightCol: "n_raked", // fallback only; per-key weights resolved from sub-variable weightCols
    subVariables: [
     // "air_new_zealand_trust",
      "fonterra_trust",
      "foodstuffs_trust",
      "genesis_trust",
    ],
    subLabels: {
     // air_new_zealand_trust: COMPANY_META.air_new_zealand.label,
      fonterra_trust:        COMPANY_META.fonterra.label,
      foodstuffs_trust:      COMPANY_META.foodstuffs.label,
      genesis_trust:         COMPANY_META.genesis.label,
    },
    subColors: {
     // air_new_zealand_trust: COMPANY_META.air_new_zealand.color,
      fonterra_trust:        COMPANY_META.fonterra.color,
      foodstuffs_trust:      COMPANY_META.foodstuffs.color,
      genesis_trust:         COMPANY_META.genesis.color,
    },
    // Flat union — used to fetch all trust data in one go
    keys: [
      //"air_new_zealand_trust_completely_trust", "air_new_zealand_trust_somewhat_trust", "air_new_zealand_trust_neither", "air_new_zealand_trust_somewhat_distrust", "air_new_zealand_trust_completely_distrust", "air_new_zealand_trust_DK",
      "fonterra_trust_completely_trust",        "fonterra_trust_somewhat_trust",        "fonterra_trust_neither",        "fonterra_trust_somewhat_distrust",        "fonterra_trust_completely_distrust",        "fonterra_trust_DK",
      "foodstuffs_trust_completely_trust",      "foodstuffs_trust_somewhat_trust",      "foodstuffs_trust_neither",      "foodstuffs_trust_somewhat_distrust",      "foodstuffs_trust_completely_distrust",      "foodstuffs_trust_DK",
      "genesis_trust_completely_trust",         "genesis_trust_somewhat_trust",         "genesis_trust_neither",         "genesis_trust_somewhat_distrust",         "genesis_trust_completely_distrust",         "genesis_trust_DK",
    ],
  },

  // ===========================================================================
  // TRUST — individual likerts per company (hidden from main dropdown)
  // ===========================================================================

  air_new_zealand_trust: {
    type: "likert",
    hidden: true,
    label: "Trust in Air New Zealand",
    fulltext: "To what extent do you trust or distrust the following companies? Air New Zealand",
    weightCol: "n_familiar_air_new_zealand",
    keys: [
      "air_new_zealand_trust_completely_trust",
      "air_new_zealand_trust_somewhat_trust",
      "air_new_zealand_trust_neither",
      "air_new_zealand_trust_somewhat_distrust",
      "air_new_zealand_trust_completely_distrust",
      "air_new_zealand_trust_DK",
    ],
    labels: {
      air_new_zealand_trust_completely_trust:    "Completely trust",
      air_new_zealand_trust_somewhat_trust:      "Somewhat trust",
      air_new_zealand_trust_neither:             "Neither",
      air_new_zealand_trust_somewhat_distrust:   "Somewhat distrust",
      air_new_zealand_trust_completely_distrust: "Completely distrust",
      air_new_zealand_trust_DK:                  "Don't know",
    },
    polarity: {
      pos: ["air_new_zealand_trust_completely_trust", "air_new_zealand_trust_somewhat_trust"],
      neu: "air_new_zealand_trust_neither",
      neg: ["air_new_zealand_trust_somewhat_distrust", "air_new_zealand_trust_completely_distrust"],
      dk:  "air_new_zealand_trust_DK",
    },
  },

  fonterra_trust: {
    type: "likert",
    hidden: true,
    label: "Trust in Fonterra",
    fulltext: "To what extent do you trust or distrust the following companies? Fonterra",
    weightCol: "n_familiar_fonterra",
    keys: [
      "fonterra_trust_completely_trust",
      "fonterra_trust_somewhat_trust",
      "fonterra_trust_neither",
      "fonterra_trust_somewhat_distrust",
      "fonterra_trust_completely_distrust",
      "fonterra_trust_DK",
    ],
    labels: {
      fonterra_trust_completely_trust:    "Completely trust",
      fonterra_trust_somewhat_trust:      "Somewhat trust",
      fonterra_trust_neither:             "Neither",
      fonterra_trust_somewhat_distrust:   "Somewhat distrust",
      fonterra_trust_completely_distrust: "Completely distrust",
      fonterra_trust_DK:                  "Don't know",
    },
    polarity: {
      pos: ["fonterra_trust_completely_trust", "fonterra_trust_somewhat_trust"],
      neu: "fonterra_trust_neither",
      neg: ["fonterra_trust_somewhat_distrust", "fonterra_trust_completely_distrust"],
      dk:  "fonterra_trust_DK",
    },
  },

  foodstuffs_trust: {
    type: "likert",
    hidden: true,
    label: "Trust in Foodstuffs",
    fulltext: "To what extent do you trust or distrust the following companies? Foodstuffs",
    weightCol: "n_familiar_foodstuffs",
    keys: [
      "foodstuffs_trust_completely_trust",
      "foodstuffs_trust_somewhat_trust",
      "foodstuffs_trust_neither",
      "foodstuffs_trust_somewhat_distrust",
      "foodstuffs_trust_completely_distrust",
      "foodstuffs_trust_DK",
    ],
    labels: {
      foodstuffs_trust_completely_trust:    "Completely trust",
      foodstuffs_trust_somewhat_trust:      "Somewhat trust",
      foodstuffs_trust_neither:             "Neither",
      foodstuffs_trust_somewhat_distrust:   "Somewhat distrust",
      foodstuffs_trust_completely_distrust: "Completely distrust",
      foodstuffs_trust_DK:                  "Don't know",
    },
    polarity: {
      pos: ["foodstuffs_trust_completely_trust", "foodstuffs_trust_somewhat_trust"],
      neu: "foodstuffs_trust_neither",
      neg: ["foodstuffs_trust_somewhat_distrust", "foodstuffs_trust_completely_distrust"],
      dk:  "foodstuffs_trust_DK",
    },
  },

  genesis_trust: {
    type: "likert",
    hidden: true,
    label: "Trust in Genesis Energy",
    fulltext: "To what extent do you trust or distrust the following companies? Genesis Energy",
    weightCol: "n_familiar_genesis",
    keys: [
      "genesis_trust_completely_trust",
      "genesis_trust_somewhat_trust",
      "genesis_trust_neither",
      "genesis_trust_somewhat_distrust",
      "genesis_trust_completely_distrust",
      "genesis_trust_DK",
    ],
    labels: {
      genesis_trust_completely_trust:    "Completely trust",
      genesis_trust_somewhat_trust:      "Somewhat trust",
      genesis_trust_neither:             "Neither",
      genesis_trust_somewhat_distrust:   "Somewhat distrust",
      genesis_trust_completely_distrust: "Completely distrust",
      genesis_trust_DK:                  "Don't know",
    },
    polarity: {
      pos: ["genesis_trust_completely_trust", "genesis_trust_somewhat_trust"],
      neu: "genesis_trust_neither",
      neg: ["genesis_trust_somewhat_distrust", "genesis_trust_completely_distrust"],
      dk:  "genesis_trust_DK",
    },
  },

  // ===========================================================================
  // APPROVAL — likert_grid parent (references sub-variables below)
  // ===========================================================================

  company_approval: {
    type: "likert_grid",
    label: "Approval of businesses",
    fulltext: "To what extent do you approve or disapprove of the activities in New Zealand of the following companies?",
    weightCol: "n_raked", // fallback only; per-key weights resolved from sub-variable weightCols
    subVariables: [
     // "air_new_zealand_approval",
      "fonterra_approval",
      "foodstuffs_approval",
      "genesis_approval",
    ],
    subLabels: {
     // air_new_zealand_approval: COMPANY_META.air_new_zealand.label,
      fonterra_approval:        COMPANY_META.fonterra.label,
      foodstuffs_approval:      COMPANY_META.foodstuffs.label,
      genesis_approval:         COMPANY_META.genesis.label,
    },
    subColors: {
     // air_new_zealand_approval: COMPANY_META.air_new_zealand.color,
      fonterra_approval:        COMPANY_META.fonterra.color,
      foodstuffs_approval:      COMPANY_META.foodstuffs.color,
      genesis_approval:         COMPANY_META.genesis.color,
    },
    keys: [
     // "air_new_zealand_approve_strongly_approve", "air_new_zealand_approve_somewhat_approve", "air_new_zealand_approve_neither", "air_new_zealand_approve_somewhat_disapprove", "air_new_zealand_approve_strongly_disapprove", "air_new_zealand_approve_DK",
      "fonterra_approve_strongly_approve",        "fonterra_approve_somewhat_approve",        "fonterra_approve_neither",        "fonterra_approve_somewhat_disapprove",        "fonterra_approve_strongly_disapprove",        "fonterra_approve_DK",
      "foodstuffs_approve_strongly_approve",      "foodstuffs_approve_somewhat_approve",      "foodstuffs_approve_neither",      "foodstuffs_approve_somewhat_disapprove",      "foodstuffs_approve_strongly_disapprove",      "foodstuffs_approve_DK",
      "genesis_approve_strongly_approve",         "genesis_approve_somewhat_approve",         "genesis_approve_neither",         "genesis_approve_somewhat_disapprove",         "genesis_approve_strongly_disapprove",         "genesis_approve_DK",
    ],
  },

  // ===========================================================================
  // APPROVAL — individual likerts per company (hidden from main dropdown)
  // ===========================================================================

  air_new_zealand_approval: {
    type: "likert",
    hidden: true,
    label: "Approval of Air New Zealand",
    fulltext: "To what extent do you approve or disapprove of the activities in New Zealand of the following companies? Air New Zealand",
    weightCol: "n_familiar_air_new_zealand",
    keys: [
      "air_new_zealand_approve_strongly_approve",
      "air_new_zealand_approve_somewhat_approve",
      "air_new_zealand_approve_neither",
      "air_new_zealand_approve_somewhat_disapprove",
      "air_new_zealand_approve_strongly_disapprove",
      "air_new_zealand_approve_DK",
    ],
    labels: {
      air_new_zealand_approve_strongly_approve:    "Strongly approve",
      air_new_zealand_approve_somewhat_approve:    "Somewhat approve",
      air_new_zealand_approve_neither:             "Neither",
      air_new_zealand_approve_somewhat_disapprove: "Somewhat disapprove",
      air_new_zealand_approve_strongly_disapprove: "Strongly disapprove",
      air_new_zealand_approve_DK:                  "Don't know",
    },
    polarity: {
      pos: ["air_new_zealand_approve_strongly_approve", "air_new_zealand_approve_somewhat_approve"],
      neu: "air_new_zealand_approve_neither",
      neg: ["air_new_zealand_approve_somewhat_disapprove", "air_new_zealand_approve_strongly_disapprove"],
      dk:  "air_new_zealand_approve_DK",
    },
  },

  fonterra_approval: {
    type: "likert",
    hidden: true,
    label: "Approval of Fonterra",
    fulltext: "To what extent do you approve or disapprove of the activities in New Zealand of the following companies? Fonterra",
    weightCol: "n_familiar_fonterra",
    keys: [
      "fonterra_approve_strongly_approve",
      "fonterra_approve_somewhat_approve",
      "fonterra_approve_neither",
      "fonterra_approve_somewhat_disapprove",
      "fonterra_approve_strongly_disapprove",
      "fonterra_approve_DK",
    ],
    labels: {
      fonterra_approve_strongly_approve:    "Strongly approve",
      fonterra_approve_somewhat_approve:    "Somewhat approve",
      fonterra_approve_neither:             "Neither",
      fonterra_approve_somewhat_disapprove: "Somewhat disapprove",
      fonterra_approve_strongly_disapprove: "Strongly disapprove",
      fonterra_approve_DK:                  "Don't know",
    },
    polarity: {
      pos: ["fonterra_approve_strongly_approve", "fonterra_approve_somewhat_approve"],
      neu: "fonterra_approve_neither",
      neg: ["fonterra_approve_somewhat_disapprove", "fonterra_approve_strongly_disapprove"],
      dk:  "fonterra_approve_DK",
    },
  },

  foodstuffs_approval: {
    type: "likert",
    hidden: true,
    label: "Approval of Foodstuffs",
    fulltext: "To what extent do you approve or disapprove of the activities in New Zealand of the following companies? Foodstuffs",
    weightCol: "n_familiar_foodstuffs",
    keys: [
      "foodstuffs_approve_strongly_approve",
      "foodstuffs_approve_somewhat_approve",
      "foodstuffs_approve_neither",
      "foodstuffs_approve_somewhat_disapprove",
      "foodstuffs_approve_strongly_disapprove",
      "foodstuffs_approve_DK",
    ],
    labels: {
      foodstuffs_approve_strongly_approve:    "Strongly approve",
      foodstuffs_approve_somewhat_approve:    "Somewhat approve",
      foodstuffs_approve_neither:             "Neither",
      foodstuffs_approve_somewhat_disapprove: "Somewhat disapprove",
      foodstuffs_approve_strongly_disapprove: "Strongly disapprove",
      foodstuffs_approve_DK:                  "Don't know",
    },
    polarity: {
      pos: ["foodstuffs_approve_strongly_approve", "foodstuffs_approve_somewhat_approve"],
      neu: "foodstuffs_approve_neither",
      neg: ["foodstuffs_approve_somewhat_disapprove", "foodstuffs_approve_strongly_disapprove"],
      dk:  "foodstuffs_approve_DK",
    },
  },

  genesis_approval: {
    type: "likert",
    hidden: true,
    label: "Approval of Genesis Energy",
    fulltext: "To what extent do you approve or disapprove of the activities in New Zealand of the following companies? Genesis Energy",
    weightCol: "n_familiar_genesis",
    keys: [
      "genesis_approve_strongly_approve",
      "genesis_approve_somewhat_approve",
      "genesis_approve_neither",
      "genesis_approve_somewhat_disapprove",
      "genesis_approve_strongly_disapprove",
      "genesis_approve_DK",
    ],
    labels: {
      genesis_approve_strongly_approve:    "Strongly approve",
      genesis_approve_somewhat_approve:    "Somewhat approve",
      genesis_approve_neither:             "Neither",
      genesis_approve_somewhat_disapprove: "Somewhat disapprove",
      genesis_approve_strongly_disapprove: "Strongly disapprove",
      genesis_approve_DK:                  "Don't know",
    },
    polarity: {
      pos: ["genesis_approve_strongly_approve", "genesis_approve_somewhat_approve"],
      neu: "genesis_approve_neither",
      neg: ["genesis_approve_somewhat_disapprove", "genesis_approve_strongly_disapprove"],
      dk:  "genesis_approve_DK",
    },
  },

};